import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { gestApi, useGestAuth } from "./GestAuthContext";

const lightColour = { green: "#16a34a", yellow: "#eab308", red: "#dc2626" };
const lightLabel = { green: "In linea", yellow: "Attenzione", red: "Critico" };
const statoLabel = { preventivo: "Preventivo", in_corso: "In corso", consegnato: "Consegnato", sospeso: "Sospeso" };

const money = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;

export function CantiereForm({ initial, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    nome: initial?.nome || "",
    cliente: initial?.cliente || "",
    indirizzo: initial?.indirizzo || "",
    responsabile: initial?.responsabile || "",
    valore_commessa: initial?.valore_commessa || 0,
    data_inizio: initial?.data_inizio ? initial.data_inizio.slice(0, 10) : "",
    data_consegna_prevista: initial?.data_consegna_prevista ? initial.data_consegna_prevista.slice(0, 10) : "",
    stato: initial?.stato || "in_corso",
    n_infissi_totali: initial?.n_infissi_totali || 0,
    note: initial?.note || "",
  }));

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        ...form,
        valore_commessa: Number(form.valore_commessa || 0),
        n_infissi_totali: Number(form.n_infissi_totali || 0),
        data_inizio: form.data_inizio || null,
        data_consegna_prevista: form.data_consegna_prevista || null,
      };
      if (initial?.id) {
        await gestApi.put(`/cantieri/${initial.id}`, payload);
        toast.success("Cantiere aggiornato");
      } else {
        await gestApi.post("/cantieri", payload);
        toast.success("Cantiere creato");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="gest-cantiere-form-modal">
      <form onSubmit={submit} className="w-full max-w-[680px] bg-white rounded-2xl overflow-hidden shadow-xl">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="text-[15px] font-semibold text-[#0a1420]">{initial ? "Modifica cantiere" : "Nuovo cantiere"}</div>
          <button type="button" onClick={onClose} className="text-[#6e6e73] hover:text-[#0a1420] text-[20px] leading-none">×</button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <div className="md:col-span-2">
            <label className="block text-[12px] text-[#6e6e73] mb-1">Nome cantiere *</label>
            <input required value={form.nome} onChange={(e) => update("nome", e.target.value)} data-testid="gest-cantiere-nome" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Cliente *</label>
            <input required value={form.cliente} onChange={(e) => update("cliente", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Responsabile</label>
            <input value={form.responsabile} onChange={(e) => update("responsabile", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[12px] text-[#6e6e73] mb-1">Indirizzo</label>
            <input value={form.indirizzo} onChange={(e) => update("indirizzo", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Data inizio</label>
            <input type="date" value={form.data_inizio} onChange={(e) => update("data_inizio", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Consegna prevista</label>
            <input type="date" value={form.data_consegna_prevista} onChange={(e) => update("data_consegna_prevista", e.target.value)} data-testid="gest-cantiere-deadline" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Stato</label>
            <select value={form.stato} onChange={(e) => update("stato", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none bg-white">
              {Object.entries(statoLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">N° infissi totali *</label>
            <input type="number" min="0" value={form.n_infissi_totali} onChange={(e) => update("n_infissi_totali", e.target.value)} data-testid="gest-cantiere-n-infissi" className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div>
            <label className="block text-[12px] text-[#6e6e73] mb-1">Valore commessa (€)</label>
            <input type="number" min="0" step="0.01" value={form.valore_commessa} onChange={(e) => update("valore_commessa", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[12px] text-[#6e6e73] mb-1">Note</label>
            <textarea rows={3} value={form.note} onChange={(e) => update("note", e.target.value)} className="w-full border border-black/10 rounded-lg px-3 py-2 text-[14px] focus:border-[#0a1420] outline-none resize-none" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-black/5 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[13px] text-[#6e6e73] hover:text-[#0a1420]">Annulla</button>
          <button type="submit" disabled={busy} data-testid="gest-cantiere-save" className="px-5 py-2 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432] disabled:opacity-60">
            {busy ? "Salvataggio…" : "Salva"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function GestCantieri() {
  const { canSeeMoney, user } = useGestAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const canEdit = user?.role === "admin" || user?.role === "ufficio";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await gestApi.get("/cantieri");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento cantieri");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (c, ev) => {
    ev?.preventDefault();
    ev?.stopPropagation();
    setEditing(c);
    setShowForm(true);
  };

  const del = async (c, ev) => {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (!window.confirm(`Eliminare il cantiere "${c.nome}"? Verranno rimossi anche produzione, posa, costi, ricavi e incassi.`)) return;
    try {
      await gestApi.delete(`/cantieri/${c.id}`);
      toast.success("Cantiere eliminato");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  };

  const filtered = filter === "all" ? rows : rows.filter((r) => r.stato === filter);

  return (
    <div className="p-5 md:p-8 max-w-[1400px] mx-auto" data-testid="gest-cantieri-page">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-[12px] uppercase tracking-widest text-[#6e6e73]">Cantieri</div>
          <h1 className="text-[26px] md:text-[34px] font-semibold tracking-[-0.02em] text-[#0a1420]">Tutti i cantieri</h1>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="gest-cantiere-new"
            className="px-4 py-2.5 bg-[#0a1420] text-white rounded-lg text-[13px] font-semibold hover:bg-[#1a2432]"
          >
            + Nuovo cantiere
          </button>
        )}
      </div>

      {/* filter tabs */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[["all", "Tutti"], ...Object.entries(statoLabel)].map(([k, v]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`gest-cantieri-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-[12px] transition-colors ${
              filter === k ? "bg-[#0a1420] text-white" : "bg-white border border-black/5 text-[#6e6e73] hover:text-[#0a1420]"
            }`}
          >
            {v}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#6e6e73]">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center text-[13px] text-[#6e6e73] border border-black/5">
          Nessun cantiere in questa vista.
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/[0.04] overflow-hidden">
          <div className="hidden md:grid grid-cols-[24px_1.4fr_1fr_0.9fr_0.7fr_0.7fr_0.9fr_120px] gap-3 px-5 py-3 text-[11px] uppercase tracking-widest text-[#6e6e73] border-b border-black/5">
            <div></div>
            <div>Cantiere</div>
            <div>Cliente</div>
            <div>Responsabile</div>
            <div className="text-right">Prod</div>
            <div className="text-right">Posa</div>
            <div className="text-right">{canSeeMoney ? "Margine" : "Consegna"}</div>
            <div className="text-right">{canEdit ? "Azioni" : ""}</div>
          </div>
          <div className="divide-y divide-black/5">
            {filtered.map((c) => (
              <div
                key={c.id}
                data-testid={`gest-cantiere-item-${c.id}`}
                onClick={() => nav(`/gestione/cantieri/${c.id}`)}
                className="grid grid-cols-[24px_1fr_auto] md:grid-cols-[24px_1.4fr_1fr_0.9fr_0.7fr_0.7fr_0.9fr_120px] gap-3 px-5 py-4 hover:bg-[#f5f7fb] transition-colors items-center cursor-pointer"
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: lightColour[c.kpi.light], boxShadow: `0 0 0 4px ${lightColour[c.kpi.light]}22` }} title={lightLabel[c.kpi.light]} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[14px] font-medium text-[#0a1420] truncate">{c.nome}</div>
                    {(c.kpi.n_alerts || 0) > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-100 font-medium">
                        {c.kpi.n_alerts}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-[#6e6e73] truncate md:hidden">{c.cliente} · {statoLabel[c.stato]}</div>
                </div>
                <div className="hidden md:block text-[13px] text-[#0a1420] truncate">{c.cliente}</div>
                <div className="hidden md:block text-[13px] text-[#6e6e73] truncate">{c.responsabile || "—"}</div>
                <div className="hidden md:block text-right text-[13px] text-[#0a1420] font-semibold">{c.kpi.prod_pct}%</div>
                <div className="hidden md:block text-right text-[13px] text-[#0a1420] font-semibold">{c.kpi.posa_pct}%</div>
                <div className="hidden md:block text-right text-[13px] text-[#0a1420] font-semibold">
                  {canSeeMoney && c.kpi.margine_pct !== undefined ? `${c.kpi.margine_pct}%` : (c.data_consegna_prevista?.slice(0, 10) || "—")}
                </div>
                {canEdit ? (
                  <div className="flex items-center justify-end gap-1 md:gap-2">
                    <button
                      onClick={(e) => openEdit(c, e)}
                      data-testid={`gest-cantiere-edit-${c.id}`}
                      title="Modifica"
                      className="px-2 py-1 text-[11px] text-[#0a1420] border border-black/10 rounded-md hover:bg-[#f5f7fb]"
                    >
                      Modifica
                    </button>
                    <button
                      onClick={(e) => del(c, e)}
                      data-testid={`gest-cantiere-del-${c.id}`}
                      title="Elimina"
                      className="px-2 py-1 text-[11px] text-red-600 border border-red-100 rounded-md hover:bg-red-50"
                    >
                      Elimina
                    </button>
                  </div>
                ) : (
                  <div className="hidden md:block text-right text-[#0a1420]/40">›</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <CantiereForm
          initial={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </div>
  );
}
